import { useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const LOCAL_LABEL = { in_loco: 'In loco', laboratorio: 'Laboratório', ambos: 'Ambos' };

const MENSAGEM_PADRAO = (nome) =>
`Prezados,

Solicitamos orçamento para calibração dos instrumentos listados na planilha em anexo, conforme especificações de faixas, pontos de calibração e EMP indicados.

Favor incluir no orçamento:
- Prazo de entrega do certificado
- Validade do certificado de calibração
- Rastreabilidade INMETRO / RBC

Aguardamos retorno até [DATA].

Atenciosamente,
${nome || ''}`.trim();

const ASSUNTO_PADRAO = 'Solicitação de Orçamento – Calibração de Instrumentos';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}

function nextCalColor(inst) {
  if (inst.calibrationStatus === 'vencido')  return 'text-red-400 font-medium';
  if (inst.calibrationStatus === 'a_vencer') return 'text-yellow-400 font-medium';
  return 'text-slate-400';
}

function abrirMailto(emails, assunto, mensagem) {
  const link = `mailto:${emails}?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(mensagem + '\n\n[Planilha Excel em anexo]')}`;
  const a = document.createElement('a');
  a.href = link;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function copiarMensagem(emails, assunto, mensagem) {
  const texto = `Para: ${emails}\nAssunto: ${assunto}\n\n${mensagem}\n\n[Planilha Excel em anexo]`;
  await navigator.clipboard.writeText(texto);
}

async function baixarExcelById(id) {
  const resp = await api.get(`/orcamentos/${id}/excel`, { responseType: 'blob' });
  const url  = URL.createObjectURL(resp.data);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `Cotacao_${id.slice(-6)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function StatusBadge({ status }) {
  const cls = status === 'enviado'
    ? 'bg-green-900/50 text-green-400 border border-green-700'
    : 'bg-slate-700/50 text-slate-400 border border-slate-600';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status === 'enviado' ? 'Enviado' : 'Rascunho'}
    </span>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function OrcamentosPage() {
  const { user } = useAuth();
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modalNovo, setModalNovo]       = useState(false);
  const [visualizando, setVisualizando] = useState(null); // id da solicitação

  async function carregar() {
    try {
      const { data } = await api.get('/orcamentos');
      setSolicitacoes(data.dados || []);
    } catch { /* ignore */ }
    setLoading(false);
  }

  useEffect(() => { carregar(); }, []);

  async function excluir(id) {
    if (!window.confirm('Excluir esta solicitação?')) return;
    await api.delete(`/orcamentos/${id}`);
    carregar();
  }

  return (
    <div className="min-h-screen bg-[var(--surface-1)] text-slate-200 p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">Solicitações de Orçamento</h2>
          <p className="text-sm text-slate-400 mt-0.5">Gere pedidos de cotação para calibração de instrumentos</p>
        </div>
        <button
          onClick={() => setModalNovo(true)}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-500 transition-colors"
        >
          + Nova Solicitação
        </button>
      </div>

      {/* Tabela */}
      <div className="bg-[var(--surface-1)] border border-[var(--surface-3)] rounded-2xl overflow-hidden">
        {loading ? (
          <p className="p-8 text-slate-400 text-sm">Carregando...</p>
        ) : solicitacoes.length === 0 ? (
          <div className="py-16 text-center">
            <div className="text-5xl mb-4">📋</div>
            <p className="text-slate-300 text-sm font-medium">Nenhuma solicitação criada ainda.</p>
            <p className="text-slate-500 text-xs mt-1 mb-5">Selecione os instrumentos, os laboratórios e gere a planilha de cotação.</p>
            <button
              onClick={() => setModalNovo(true)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-500"
            >
              Criar primeira solicitação
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--surface-3)] text-xs text-slate-500 uppercase tracking-wider">
                <th className="text-left px-5 py-3 font-medium">Data</th>
                <th className="text-left px-5 py-3 font-medium">Laboratórios</th>
                <th className="text-left px-5 py-3 font-medium">Instrumentos</th>
                <th className="text-left px-5 py-3 font-medium">Criado por</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {solicitacoes.map(s => (
                <tr key={s.id} className="border-b border-[var(--surface-2)] hover:bg-[var(--surface-2)] transition-colors">
                  <td className="px-5 py-3.5 text-slate-300">{fmtDate(s.createdAt)}</td>
                  <td className="px-5 py-3.5 text-slate-300 max-w-[240px]">
                    <span className="truncate block">{s.laboratorios?.map(l => l.nome).join(', ') || '—'}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="px-2 py-0.5 bg-blue-900/40 text-blue-300 rounded text-xs font-medium border border-blue-800/50">
                      {s.instrumentoIds?.length || 0} instr.
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-400">{s.criadoPorNome}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={s.status} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3 justify-end">
                      <button
                        onClick={() => setVisualizando(s.id)}
                        className="text-xs text-slate-400 hover:text-white transition-colors"
                      >
                        Visualizar
                      </button>
                      <button
                        onClick={() => baixarExcelById(s.id)}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        ⬇ Excel
                      </button>
                      {user?.role === 'supervisor' && (
                        <button
                          onClick={() => excluir(s.id)}
                          className="text-xs text-red-500 hover:text-red-400 transition-colors"
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalNovo && (
        <ModalNovaSolicitacao
          user={user}
          onClose={() => setModalNovo(false)}
          onSalvo={() => { setModalNovo(false); carregar(); }}
        />
      )}

      {visualizando && (
        <ModalVisualizacao
          id={visualizando}
          onClose={() => setVisualizando(null)}
          onSalvo={() => { setVisualizando(null); carregar(); }}
        />
      )}
    </div>
  );
}

// ─── Modal Visualizar / Editar ────────────────────────────────────────────────
function ModalVisualizacao({ id, onClose, onSalvo }) {
  const [dados,    setDados]    = useState(null);
  const [assunto,  setAssunto]  = useState('');
  const [mensagem, setMensagem] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [salvo,    setSalvo]    = useState(false);
  const [copiado,  setCopiado]  = useState(false);

  useEffect(() => {
    api.get(`/orcamentos/${id}`).then(r => {
      setDados(r.data);
      setAssunto(r.data.assunto || '');
      setMensagem(r.data.mensagem || '');
    });
  }, [id]);

  const emailsTo = dados?.laboratorios
    ?.map(l => l.email).filter(Boolean).join('; ') || '';

  async function salvarAlteracoes() {
    setSalvando(true);
    await api.put(`/orcamentos/${id}`, { assunto, mensagem });
    setSalvando(false);
    setSalvo(true);
    setTimeout(() => setSalvo(false), 2000);
  }

  async function handleEmail() {
    await api.put(`/orcamentos/${id}`, { assunto, mensagem, status: 'enviado' });
    const emails = dados?.laboratorios?.map(l => l.email).filter(Boolean).join(',') || '';
    abrirMailto(emails, assunto, mensagem);
    onSalvo();
  }

  async function handleExcel() {
    await baixarExcelById(id);
  }

  if (!dados) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="bg-[var(--surface-1)] border border-[var(--surface-3)] rounded-2xl p-8 text-slate-400 text-sm">
          Carregando...
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-[var(--surface-1)] border border-[var(--surface-3)] rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-[var(--surface-3)] flex-shrink-0 flex justify-between items-start">
          <div>
            <h3 className="text-base font-semibold text-white">Solicitação de Orçamento</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {fmtDate(dados.createdAt)} · {dados.criadoPorNome} · <StatusBadge status={dados.status} />
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white text-lg leading-none ml-4">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* Labs */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Para</label>
            <div className="bg-[var(--surface-2)] border border-[var(--surface-3)] rounded-lg px-3 py-2 text-sm text-slate-300 min-h-[36px]">
              {emailsTo || (
                <span className="text-amber-400 text-xs">⚠ Nenhum laboratório com e-mail cadastrado</span>
              )}
            </div>
            {dados.laboratorios?.length > 0 && (
              <p className="text-xs text-slate-500 mt-1">
                {dados.laboratorios.map(l => l.nome).join(' · ')} · {dados.instrumentoIds?.length || 0} instrumento(s)
              </p>
            )}
          </div>

          {/* Assunto */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Assunto</label>
            <input
              value={assunto}
              onChange={e => setAssunto(e.target.value)}
              className="w-full bg-[var(--surface-2)] border border-[var(--surface-3)] rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Mensagem */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Mensagem</label>
            <textarea
              value={mensagem}
              onChange={e => setMensagem(e.target.value)}
              rows={12}
              className="w-full bg-[var(--surface-2)] border border-[var(--surface-3)] rounded-lg px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>

          {/* Anexo */}
          <div className="flex items-center gap-2 text-xs text-slate-500 bg-[var(--surface-2)] border border-[var(--surface-3)] rounded-lg px-3 py-2">
            <span>📎</span>
            <span>Cotacao_{id.slice(-6)}.xlsx — {dados.instrumentoIds?.length || 0} instrumento(s)</span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--surface-3)] flex-shrink-0 flex justify-between items-center">
          <button
            onClick={salvarAlteracoes}
            disabled={salvando}
            className="px-3 py-1.5 text-xs border border-[var(--surface-4)] rounded-lg text-slate-400 hover:text-white hover:border-slate-500 transition-colors disabled:opacity-50"
          >
            {salvo ? '✓ Salvo' : salvando ? 'Salvando...' : 'Salvar alterações'}
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleExcel}
              className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg transition-colors"
            >
              ⬇ Baixar Excel
            </button>
            <button
              onClick={async () => {
                await copiarMensagem(emailsTo, assunto, mensagem);
                setCopiado(true);
                setTimeout(() => setCopiado(false), 2500);
              }}
              className="px-4 py-2 text-sm border border-[var(--surface-4)] rounded-lg text-slate-300 hover:bg-[var(--surface-2)] transition-colors"
              title="Copia Para, Assunto e Mensagem — cole no Gmail ou Outlook Web"
            >
              {copiado ? '✓ Copiado!' : '📋 Copiar'}
            </button>
            <button
              onClick={handleEmail}
              disabled={!emailsTo}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors disabled:opacity-40"
              title={!emailsTo ? 'Nenhum laboratório com e-mail' : 'Abre cliente de e-mail instalado (Outlook, Thunderbird...)'}
            >
              ✉ Abrir no E-mail
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal Nova Solicitação (3 passos) ────────────────────────────────────────
function ModalNovaSolicitacao({ user, onClose, onSalvo }) {
  const [passo, setPasso] = useState(1);

  const [instrumentos, setInstrumentos] = useState([]);
  const [loadingInst,  setLoadingInst]  = useState(true);
  const [selecionados, setSelecionados] = useState(new Set());
  const [filtroSetor,  setFiltroSetor]  = useState('');
  const [filtroLocal,  setFiltroLocal]  = useState('');
  const [busca,        setBusca]        = useState('');

  const [labs,      setLabs]      = useState([]);
  const [loadingLabs, setLoadingLabs] = useState(true);
  const [labsSelec, setLabsSelec] = useState(new Set());

  const [assunto,  setAssunto]  = useState(ASSUNTO_PADRAO);
  const [mensagem, setMensagem] = useState(MENSAGEM_PADRAO(user?.name));
  const [salvando, setSalvando] = useState(false);
  const [idSalvo,  setIdSalvo]  = useState(null);
  const [erro,     setErro]     = useState('');
  const [copiado,  setCopiado]  = useState(false);

  useEffect(() => {
    api.get('/instruments', { params: { limit: 500 } })
      .then(r => setInstrumentos(r.data.dados || []))
      .catch(() => {})
      .finally(() => setLoadingInst(false));

    api.get('/laboratorios')
      .then(r => setLabs(Array.isArray(r.data) ? r.data : (r.data.dados || [])))
      .catch(() => {})
      .finally(() => setLoadingLabs(false));
  }, []);

  const setores = useMemo(
    () => [...new Set(instrumentos.map(i => i.sector).filter(Boolean))].sort(),
    [instrumentos],
  );

  const instFiltrados = useMemo(() => instrumentos.filter(i => {
    if (filtroSetor && i.sector !== filtroSetor) return false;
    if (filtroLocal && i.localCalibracao !== filtroLocal) return false;
    if (busca) {
      const b = busca.toLowerCase();
      return i.tag?.toLowerCase().includes(b) || i.description?.toLowerCase().includes(b);
    }
    return true;
  }), [instrumentos, filtroSetor, filtroLocal, busca]);

  const todosSelec = instFiltrados.length > 0 && instFiltrados.every(i => selecionados.has(i.id));

  function toggleInst(id) {
    setSelecionados(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleTodos() {
    if (todosSelec) {
      setSelecionados(prev => { const n = new Set(prev); instFiltrados.forEach(i => n.delete(i.id)); return n; });
    } else {
      setSelecionados(prev => { const n = new Set(prev); instFiltrados.forEach(i => n.add(i.id)); return n; });
    }
  }
  function toggleLab(id) {
    setLabsSelec(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function salvar(status) {
    setSalvando(true);
    setErro('');
    try {
      if (idSalvo) {
        await api.put(`/orcamentos/${idSalvo}`, { status, assunto, mensagem });
        return idSalvo;
      }
      const { data } = await api.post('/orcamentos', {
        instrumentoIds: [...selecionados],
        laboratorioIds: [...labsSelec],
        assunto,
        mensagem,
        status,
      });
      setIdSalvo(data.id);
      return data.id;
    } catch {
      setErro('Erro ao salvar. Tente novamente.');
      return null;
    } finally {
      setSalvando(false);
    }
  }

  async function handleBaixarExcel() {
    const id = await salvar('rascunho');
    if (!id) return;
    await baixarExcelById(id);
    onSalvo();
  }

  async function handleAbrirEmail() {
    const id = await salvar('enviado');
    if (!id) return;
    const labsArr = labs.filter(l => labsSelec.has(l.id));
    const emails  = labsArr.map(l => l.email).filter(Boolean).join(',');
    abrirMailto(emails, assunto, mensagem);
    onSalvo();
  }

  async function handleSalvarRascunho() {
    const id = await salvar('rascunho');
    if (id) onSalvo();
  }

  const labsArr  = labs.filter(l => labsSelec.has(l.id));
  const emailsTo = labsArr.map(l => l.email).filter(Boolean).join('; ');
  const STEPS    = ['Instrumentos', 'Laboratórios', 'Mensagem'];

  const inputCls  = 'bg-[var(--surface-2)] border border-[var(--surface-3)] rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500';
  const selectCls = inputCls;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-[var(--surface-1)] border border-[var(--surface-3)] rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-[var(--surface-3)] flex-shrink-0">
          <div className="flex justify-between items-center mb-5">
            <h3 className="text-base font-semibold text-white">Nova Solicitação de Orçamento</h3>
            <button onClick={onClose} className="text-slate-500 hover:text-white text-lg leading-none">✕</button>
          </div>
          {/* Stepper */}
          <div className="flex items-center">
            {STEPS.map((label, i) => {
              const n = i + 1;
              return (
                <div key={n} className="flex items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors ${
                    passo === n ? 'bg-blue-600 border-blue-600 text-white' :
                    passo  > n ? 'bg-green-600 border-green-600 text-white' :
                                 'border-slate-600 text-slate-500'
                  }`}>
                    {passo > n ? '✓' : n}
                  </div>
                  <span className={`ml-2 text-sm ${passo === n ? 'text-blue-400 font-medium' : 'text-slate-500'}`}>{label}</span>
                  {n < STEPS.length && <div className="w-10 h-px bg-[var(--surface-3)] mx-3" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Passo 1 */}
          {passo === 1 && (
            <div>
              <div className="flex flex-wrap gap-3 mb-4">
                <input
                  placeholder="Buscar TAG ou descrição..."
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  className={`${inputCls} flex-1 min-w-[180px] placeholder-slate-500`}
                />
                <select value={filtroSetor} onChange={e => setFiltroSetor(e.target.value)} className={selectCls}>
                  <option value="">Todos os setores</option>
                  {setores.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filtroLocal} onChange={e => setFiltroLocal(e.target.value)} className={selectCls}>
                  <option value="">Todos os locais</option>
                  <option value="in_loco">In loco</option>
                  <option value="laboratorio">Laboratório</option>
                  <option value="ambos">Ambos</option>
                </select>
              </div>

              {loadingInst ? (
                <p className="text-slate-400 text-sm py-6">Carregando instrumentos...</p>
              ) : (
                <div className="border border-[var(--surface-3)] rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[var(--surface-2)] border-b border-[var(--surface-3)] text-xs text-slate-500 uppercase tracking-wider">
                        <th className="px-3 py-2.5 w-8">
                          <input type="checkbox" checked={todosSelec} onChange={toggleTodos} className="rounded" />
                        </th>
                        <th className="text-left px-3 py-2.5 font-medium">TAG</th>
                        <th className="text-left px-3 py-2.5 font-medium">Descrição</th>
                        <th className="text-left px-3 py-2.5 font-medium">Setor</th>
                        <th className="text-left px-3 py-2.5 font-medium">Local</th>
                        <th className="text-left px-3 py-2.5 font-medium">Próx. Calibração</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instFiltrados.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-slate-500 text-sm">
                            Nenhum instrumento encontrado com esses filtros.
                          </td>
                        </tr>
                      ) : instFiltrados.map(inst => (
                        <tr
                          key={inst.id}
                          onClick={() => toggleInst(inst.id)}
                          className={`border-b border-[var(--surface-2)] cursor-pointer transition-colors ${
                            selecionados.has(inst.id) ? 'bg-blue-900/30' : 'hover:bg-[var(--surface-2)]'
                          }`}
                        >
                          <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={selecionados.has(inst.id)} onChange={() => toggleInst(inst.id)} className="rounded" />
                          </td>
                          <td className="px-3 py-2.5 font-medium text-slate-200">{inst.tag}</td>
                          <td className="px-3 py-2.5 text-slate-400 max-w-[200px] truncate">{inst.description}</td>
                          <td className="px-3 py-2.5 text-slate-500">{inst.sector || '—'}</td>
                          <td className="px-3 py-2.5 text-slate-500">{LOCAL_LABEL[inst.localCalibracao] || '—'}</td>
                          <td className={`px-3 py-2.5 ${nextCalColor(inst)}`}>{fmtDate(inst.nextCalibrationDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Passo 2 */}
          {passo === 2 && (
            <div className="space-y-3">
              {loadingLabs ? (
                <p className="text-slate-400 text-sm">Carregando laboratórios...</p>
              ) : labs.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-slate-400 text-sm">Nenhum laboratório cadastrado.</p>
                  <p className="text-slate-500 text-xs mt-1">Adicione em Padrões → Laboratórios.</p>
                </div>
              ) : labs.map(lab => (
                <div
                  key={lab.id}
                  onClick={() => toggleLab(lab.id)}
                  className={`border rounded-xl p-4 cursor-pointer transition-all ${
                    labsSelec.has(lab.id)
                      ? 'border-blue-600 bg-blue-900/30'
                      : 'border-[var(--surface-3)] hover:border-[var(--surface-4)] hover:bg-[var(--surface-2)]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={labsSelec.has(lab.id)} onChange={() => toggleLab(lab.id)}
                      className="rounded mt-0.5 flex-shrink-0" onClick={e => e.stopPropagation()} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-200">{lab.nome}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-slate-500">
                        {lab.acreditacao && <span>📋 {lab.acreditacao}</span>}
                        {lab.email ? <span>✉ {lab.email}</span> : <span className="text-amber-400">⚠ sem e-mail</span>}
                        {lab.telefone && <span>📞 {lab.telefone}</span>}
                        {lab.contato  && <span>👤 {lab.contato}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Passo 3 */}
          {passo === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Para</label>
                <div className="bg-[var(--surface-2)] border border-[var(--surface-3)] rounded-lg px-3 py-2 text-sm min-h-[36px] text-slate-300">
                  {emailsTo || <span className="text-amber-400 text-xs">⚠ Nenhum laboratório com e-mail — link de e-mail ficará vazio.</span>}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Assunto</label>
                <input value={assunto} onChange={e => setAssunto(e.target.value)} className={inputCls + ' w-full'} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Mensagem</label>
                <textarea value={mensagem} onChange={e => setMensagem(e.target.value)} rows={11}
                  className={inputCls + ' w-full font-mono resize-none'} />
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 bg-[var(--surface-2)] border border-[var(--surface-3)] rounded-lg px-3 py-2">
                <span>📎</span>
                <span>Cotacao_Calibracao.xlsx — {selecionados.size} instrumento(s)</span>
              </div>
              {erro && <p className="text-red-400 text-xs">{erro}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--surface-3)] flex-shrink-0 flex justify-between items-center">
          <p className="text-sm text-slate-500">
            {passo === 1 && `${selecionados.size} selecionado(s)`}
            {passo === 2 && `${labsSelec.size} laboratório(s) selecionado(s)`}
          </p>
          <div className="flex gap-2">
            {passo > 1 && (
              <button onClick={() => setPasso(p => p - 1)}
                className="px-4 py-2 text-sm border border-[var(--surface-4)] rounded-lg text-slate-300 hover:bg-[var(--surface-2)] transition-colors">
                ← Voltar
              </button>
            )}
            {passo < 3 && (
              <button
                onClick={() => {
                  if (passo === 1 && selecionados.size === 0) { alert('Selecione ao menos um instrumento.'); return; }
                  setPasso(p => p + 1);
                }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors">
                Próximo →
              </button>
            )}
            {passo === 3 && (
              <>
                <button onClick={handleSalvarRascunho} disabled={salvando}
                  className="px-4 py-2 text-sm border border-[var(--surface-4)] rounded-lg text-slate-300 hover:bg-[var(--surface-2)] disabled:opacity-50 transition-colors">
                  Salvar Rascunho
                </button>
                <button onClick={handleBaixarExcel} disabled={salvando}
                  className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg disabled:opacity-50 transition-colors">
                  ⬇ Baixar Excel
                </button>
                <button
                  onClick={async () => {
                    await copiarMensagem(emailsTo, assunto, mensagem);
                    setCopiado(true);
                    setTimeout(() => setCopiado(false), 2500);
                  }}
                  className="px-4 py-2 text-sm border border-[var(--surface-4)] rounded-lg text-slate-300 hover:bg-[var(--surface-2)] transition-colors"
                  title="Copia Para, Assunto e Mensagem — cole no Gmail ou Outlook Web"
                >
                  {copiado ? '✓ Copiado!' : '📋 Copiar'}
                </button>
                <button onClick={handleAbrirEmail} disabled={salvando || !emailsTo}
                  title={!emailsTo ? 'Nenhum laboratório com e-mail' : 'Abre cliente de e-mail instalado (Outlook, Thunderbird...)'}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg disabled:opacity-40 transition-colors">
                  ✉ Abrir no E-mail
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
